import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AppDatePicker,
  AppInput,
  AppSelect,
  AppTextarea,
  MasterAddressFields,
  OutlineButton,
  PhotoUploadField,
  PrimaryButton,
} from '../../../../components/ui';
import {
  EDUCATION_OPTIONS,
  GENDER_OPTIONS,
  VERIFICATION_OPTIONS,
} from '../../../../data/master/people';
import {
  createVillager,
  setVillagerPhotoDocument,
  updateVillager,
} from '../../../../repositories/villager.repository';
import {
  listDocuments,
  uploadDocument,
} from '../../../../repositories/document.repository';
import { lookupVillages } from '../../../../repositories/lookup.repository';
import { profilePhotoFromDocuments } from '../../../../models';
import { dbIdForCode } from '../../../../utils/entityResolve';
import { ageFromDob, composeFullName } from '../../../../utils/personName';
import {
  dobMaxIso,
  dobMinIso,
  FIELD_LIMITS,
  validatePeopleForm,
} from '../../../../utils/peopleValidation';
import { formatAadhaarInput } from '../../../../utils/vendorValidation';

const emptyForm = {
  name: '',
  fatherHusbandName: '',
  surname: '',
  mobile: '',
  email: '',
  aadhaar: '',
  pan: '',
  electionCard: '',
  drivingLicense: '',
  dob: '',
  gender: 'M',
  address1: '',
  address2: '',
  villageId: '',
  pin: '',
  education: '10',
  educationDetails: '',
  photo: null,
  photoDocumentId: null,
  verificationStatus: 'Pending',
};

function personToForm(p) {
  if (!p) return emptyForm;
  return {
    name: p.name || '',
    fatherHusbandName: p.fatherHusbandName || '',
    surname: p.surname || '',
    mobile: String(p.mobile || '').replace(/\D/g, '').slice(0, 10),
    email: p.email || '',
    aadhaar: formatAadhaarInput(p.aadhaar || ''),
    pan: p.pan || '',
    electionCard: p.electionCard || '',
    drivingLicense: p.drivingLicense || '',
    dob: p.dob || '',
    gender: p.gender || 'M',
    address1: p.address1 || '',
    address2: p.address2 || '',
    villageId: p.villageId || '',
    pin: p.pin || '',
    education: p.education || '10',
    educationDetails: p.educationDetails || '',
    photo: p.photo || null,
    photoDocumentId: p.photoDocumentId ?? null,
    verificationStatus: p.verificationStatus || 'Pending',
  };
}

/**
 * Shared Add / Edit villager modal (list + detail).
 * @param {object|null} person — null = Add; object = Edit
 * @param {() => void} onSaved — refresh parent after save
 * @param {array} villages — optional preloaded villages
 */
export function VillagerFormModal({ isOpen, onClose, person = null, onSaved, villages: villagesProp }) {
  const toast = useToast();
  const editingId = person?.id || null;
  const displayCode = person?.peopleId || null;
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [villages, setVillages] = useState(villagesProp || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(personToForm(person));
    setErrors({});
    let cancelled = false;
    (async () => {
      if (!villagesProp?.length) {
        try {
          const items = await lookupVillages();
          if (!cancelled) setVillages(items || []);
        } catch (err) {
          if (!cancelled) {
            toast({
              title: err?.message || 'Failed to load villages',
              status: 'error',
              position: 'top-right',
            });
          }
        }
      } else {
        setVillages(villagesProp);
      }
      if (!person?.id || person?.photo?.url) return;
      try {
        const docs = await listDocuments({
          ownerType: 'villager',
          ownerId: person.id,
        });
        if (cancelled) return;
        const profile = profilePhotoFromDocuments(docs, person.photoDocumentId);
        if (!profile) return;
        setForm((f) => ({
          ...f,
          photoDocumentId: profile.id,
          photo: f.photo?.file ? f.photo : { name: profile.name, url: profile.url },
        }));
      } catch {
        /* preview is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, person, villagesProp, toast]);

  const fullName = composeFullName(form.name, form.fatherHusbandName, form.surname);
  const age = ageFromDob(form.dob);

  const patchForm = (partial) => {
    setForm((f) => ({ ...f, ...partial }));
    const keys = Object.keys(partial);
    if (keys.length) {
      setErrors((prev) => {
        const next = { ...prev };
        keys.forEach((k) => delete next[k]);
        return next;
      });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const nextErrors = validatePeopleForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast({
        title: 'Fix the highlighted fields',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    const composed = composeFullName(form.name, form.fatherHusbandName, form.surname);
    const villageDbId =
      person?.villageDbId && person?.villageId === form.villageId
        ? person.villageDbId
        : dbIdForCode(villages, form.villageId, { codeKey: 'villageId' });

    const payload = {
      name: form.name.trim(),
      fatherHusbandName: form.fatherHusbandName.trim(),
      surname: form.surname.trim(),
      fullName: composed || form.name.trim(),
      mobile: form.mobile.trim(),
      email: form.email.trim(),
      aadhaar: form.aadhaar.trim(),
      pan: form.pan.trim().toUpperCase(),
      electionCard: form.electionCard.trim(),
      drivingLicense: form.drivingLicense.trim(),
      dob: form.dob,
      gender: form.gender,
      address1: form.address1.trim(),
      address2: form.address2.trim(),
      villageId: form.villageId,
      villageDbId,
      pin: form.pin.trim(),
      education: form.education,
      educationDetails: form.educationDetails.trim(),
      photoDocumentId: form.photo ? form.photoDocumentId : null,
      verificationStatus: form.verificationStatus || 'Pending',
      active: form.verificationStatus !== 'Rejected',
    };

    setSaving(true);
    try {
      const saved = editingId
        ? await updateVillager(editingId, payload)
        : await createVillager({
            ...payload,
            verificationStatus: 'Pending',
            active: true,
          });

      if (form.photo?.file && saved?.id) {
        try {
          const uploaded = await uploadDocument({
            ownerType: 'villager',
            ownerId: saved.id,
            docType: 'profile_photo',
            file: form.photo.file,
          });
          await setVillagerPhotoDocument(saved.id, uploaded.id);
        } catch (uploadErr) {
          toast({
            title: 'Villager saved, but photo upload failed',
            description: uploadErr?.message,
            status: 'warning',
            position: 'top-right',
          });
        }
      }

      toast({
        title: editingId ? 'Villager updated' : 'Villager saved',
        description: editingId
          ? `${payload.fullName} · ${displayCode || editingId}`
          : `${saved.fullName} · ${saved.peopleId} · Pending verification`,
        status: 'success',
        position: 'top-right',
      });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save villager',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      blockScrollOnMount
      trapFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          {displayCode ? `Edit villager · ${displayCode}` : 'Add villager (People Master)'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
          <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              <AppInput
                label="First Name"
                isRequired
                value={form.name}
                onChange={(e) => patchForm({ name: e.target.value })}
                maxLength={FIELD_LIMITS.name.max}
                isInvalid={!!errors.name}
                errorMessage={errors.name}
              />
              <AppInput
                label="Father’s / Husband’s Name"
                value={form.fatherHusbandName}
                onChange={(e) => patchForm({ fatherHusbandName: e.target.value })}
                maxLength={FIELD_LIMITS.fatherHusbandName.max}
                isInvalid={!!errors.fatherHusbandName}
                errorMessage={errors.fatherHusbandName}
              />
              <AppInput
                label="Surname"
                value={form.surname}
                onChange={(e) => patchForm({ surname: e.target.value })}
                maxLength={FIELD_LIMITS.surname.max}
                isInvalid={!!errors.surname}
                errorMessage={errors.surname}
              />
              <AppInput
                label="Full Name"
                value={fullName}
                isReadOnly
                placeholder="From name parts"
                helperText="Auto: First + Father/Husband + Surname"
              />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
              <AppInput
                label="Mobile Number"
                isRequired
                leftAddon="+91"
                value={form.mobile}
                onChange={(e) =>
                  patchForm({ mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })
                }
                placeholder="10-digit mobile"
                maxLength={10}
                inputMode="numeric"
                isInvalid={!!errors.mobile}
                errorMessage={errors.mobile}
              />
              <AppInput
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => patchForm({ email: e.target.value })}
                maxLength={FIELD_LIMITS.email.max}
                placeholder="name@example.com"
                isInvalid={!!errors.email}
                errorMessage={errors.email}
              />
              <AppInput
                label="Aadhaar Number"
                value={form.aadhaar}
                onChange={(e) => patchForm({ aadhaar: formatAadhaarInput(e.target.value) })}
                maxLength={14}
                placeholder="XXXX-XXXX-XXXX"
                inputMode="numeric"
                isInvalid={!!errors.aadhaar}
                errorMessage={errors.aadhaar}
              />
              <AppInput
                label="PAN Number"
                value={form.pan}
                onChange={(e) =>
                  patchForm({ pan: e.target.value.toUpperCase().slice(0, 10) })
                }
                maxLength={10}
                placeholder="ABCDE1234F"
                isInvalid={!!errors.pan}
                errorMessage={errors.pan}
              />
              <AppInput
                label="Election Card Number"
                value={form.electionCard}
                onChange={(e) => patchForm({ electionCard: e.target.value })}
                maxLength={FIELD_LIMITS.electionCard.max}
                isInvalid={!!errors.electionCard}
                errorMessage={errors.electionCard}
              />
              <AppInput
                label="Driving License"
                value={form.drivingLicense}
                onChange={(e) => patchForm({ drivingLicense: e.target.value })}
                maxLength={FIELD_LIMITS.drivingLicense.max}
                isInvalid={!!errors.drivingLicense}
                errorMessage={errors.drivingLicense}
              />
              <AppDatePicker
                label="Date of Birth"
                value={form.dob}
                onChange={(e) => patchForm({ dob: e.target.value })}
                min={dobMinIso()}
                max={dobMaxIso()}
                helperText="Must be 18+ years. Shown as DD/MM/YYYY."
                isInvalid={!!errors.dob}
                errorMessage={errors.dob}
              />
              <AppInput
                label="Age"
                value={age != null ? String(age) : ''}
                isReadOnly
                placeholder="From birthdate"
              />
              <AppSelect
                label="Gender"
                isRequired
                value={form.gender}
                onChange={(v) => patchForm({ gender: v })}
                options={GENDER_OPTIONS}
                isInvalid={!!errors.gender}
                errorMessage={errors.gender}
              />
              <AppSelect
                label="Education"
                value={form.education}
                onChange={(v) => patchForm({ education: v })}
                options={EDUCATION_OPTIONS.map((c) => ({ value: c, label: c }))}
              />
              {editingId ? (
                <AppSelect
                  label="Verification"
                  value={form.verificationStatus}
                  onChange={(v) => patchForm({ verificationStatus: v })}
                  options={VERIFICATION_OPTIONS}
                />
              ) : null}
            </SimpleGrid>

            <AppTextarea
              label="Education Details"
              value={form.educationDetails}
              onChange={(e) => patchForm({ educationDetails: e.target.value })}
              rows={2}
              maxLength={FIELD_LIMITS.educationDetails.max}
              isInvalid={!!errors.educationDetails}
              errorMessage={errors.educationDetails}
            />

            <MasterAddressFields
              villages={villages}
              address1={form.address1}
              address2={form.address2}
              villageId={form.villageId}
              pin={form.pin}
              onChange={patchForm}
              errors={errors}
            />

            <PhotoUploadField
              label="Profile photo"
              helperText="Shown next to the name on the villager detail page."
              accept="image/*"
              value={form.photo}
              onChange={(photo) =>
                patchForm({
                  photo,
                  ...(photo ? {} : { photoDocumentId: null }),
                })
              }
            />
          </VStack>
        </ModalBody>
        <ModalFooter
          flexShrink={0}
          gap={2}
          borderTopWidth="1px"
          borderColor="border.subtle"
          bg="bg.surface"
          zIndex={2}
        >
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>
            Save
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
